import { Box, Typography } from "@mui/material";
import { useTranslation } from "i18n-fe";

// #3395 -- the ONE ride-detail block, shared by the open ride row (LookupRideRow)
// and the In the Ring Now box (LiveDesk). Lines, tight (no gaps between
// them), in this order: rider location, horse line, then (#3409) ONE borderless
// two-column table, FIELD NAME FIRST, values lined up in one column: SIRE, DAM,
// DAM'S SIRE, OWNER, BREEDER, COACH (#3396, only when the entry has a coach) and
// (Test of Choice only) TEST.
// The label cell is bold small caps, no colons. A row with no data is dropped,
// never a bare label. No class name, no horse name, no headings, no trainer.
// SIRE, DAM and DAM'S SIRE are always their own three rows at every tier (#3409
// dropped #3398's one-line pedigree); `omit` drops named rows.
//
// `detail` is the row.detail object built by utils/rideDetail.js buildRideDetail.
// `afterLocation` is the slot for the In the Ring Now box: its big horse name
// goes right after the location line. `sx` lets the caller place the block
// (the open row indents it under the rider name column).
const LABELLED_LINES = [
  ["sire", "detail.sire"],
  ["dam", "detail.dam"],
  ["damsSire", "detail.damsSire"],
  ["owner", "detail.owner"],
  ["breeder", "detail.breeder"],
  ["coach", "detail.coach"],
  ["test", "detail.test"],
];

export function hasRideDetail(detail) {
  return Boolean(
    detail && (detail.location || detail.horseLine || LABELLED_LINES.some(([key]) => detail[key])),
  );
}

const LABEL_SX = {
  fontWeight: 700,
  fontVariant: "all-small-caps",
  color: "text.secondary",
  letterSpacing: 0.4,
  whiteSpace: "nowrap",
  verticalAlign: "baseline",
  textAlign: "left",
  p: 0,
  pr: 1,
  width: "1%",
  border: 0,
};
const VALUE_SX = { verticalAlign: "baseline", p: 0, border: 0 };

export default function RideDetailBlock({ detail, afterLocation = null, sx, omit = [] }) {
  const { t } = useTranslation("announcer");
  if (!hasRideDetail(detail) && !afterLocation) return null;
  const rows = LABELLED_LINES.filter(([key]) => detail?.[key] && !omit.includes(key));
  return (
    <Box data-testid="ride-detail-block" sx={{ display: "grid", gap: 0, ...sx }}>
      {detail?.location && (
        <Typography variant="body2" data-testid="detail-location">
          {detail.location}
        </Typography>
      )}
      {afterLocation}
      {detail?.horseLine && (
        <Typography variant="body2" data-testid="detail-horse-line">
          {detail.horseLine}
        </Typography>
      )}
      {rows.length > 0 && (
        <Box
          component="table"
          data-testid="detail-table"
          sx={{ borderCollapse: "collapse", border: 0, borderSpacing: 0, width: "100%", tableLayout: "auto" }}
        >
          <tbody>
            {rows.map(([key, labelKey]) => (
              <tr key={key} data-testid={`detail-${key}`}>
                <Typography component="th" scope="row" variant="body2" data-testid={`detail-${key}-label`} sx={LABEL_SX}>
                  {t(labelKey)}
                </Typography>
                <Typography component="td" variant="body2" data-testid={`detail-${key}-value`} sx={VALUE_SX}>
                  {detail[key]}
                </Typography>
              </tr>
            ))}
          </tbody>
        </Box>
      )}
    </Box>
  );
}
